import { useMemo } from 'react';
import type { RunnerState } from '../components/meeting/RunnerAvatar';
import { haversineMeters } from 'common/utils/geo';

export interface LocationSample {
    latitude: number;
    longitude: number;
    timestamp: number; // unix ms
}

export interface EtaInput {
    history: LocationSample[];         // 최근 좌표 기록 (시간순, 최신이 마지막)
    destination: { latitude: number; longitude: number };
    meetingStartMs: number;            // 약속 시각 (unix ms)
    now?: number;                      // 테스트용
}

export interface EtaResult {
    distanceMeters: number;      // 집결지까지 직선 거리
    speedKmh: number;            // 최근 평균 속도
    etaMinutes: number | null;   // 도착 예상 분 (null = 계산 불가)
    arrived: boolean;            // 200m 이내
    minutesUntilStart: number;   // 약속 시각까지 남은 분 (음수 = 지난 뒤)
    projectedLateMinutes: number; // 예상 지각 분 (≥0, 정시·조기 도착시 0)
    state: RunnerState;
}

const ARRIVE_RADIUS_M = 200;
const MIN_SPEED_KMH = 1;
const MAX_SPEED_KMH = 120;
const ROUTE_DETOUR_FACTOR = 1.3; // 직선 거리 × 1.3 = 도로 거리 근사


/**
 * 최근 좌표 N개로부터 평균 속도 (km/h) 계산
 * - 샘플 2개 미만: 0
 * - 샘플 간 시간차 1초 미만: 스킵 (GPS 노이즈)
 * - 120km/h 초과 샘플: 스킵
 */
function estimateSpeedKmh(history: LocationSample[]): number {
    if (history.length < 2) return 0;
    const recent = history.slice(-5); // 최근 5개
    let totalMeters = 0;
    let totalHours = 0;
    for (let i = 1; i < recent.length; i++) {
        const a = recent[i - 1];
        const b = recent[i];
        const dtSec = (b.timestamp - a.timestamp) / 1000;
        if (dtSec < 1) continue;
        const distM = haversineMeters(a.latitude, a.longitude, b.latitude, b.longitude);
        const kmh = (distM / 1000) / (dtSec / 3600);
        if (kmh > MAX_SPEED_KMH) continue;
        totalMeters += distM;
        totalHours += dtSec / 3600;
    }
    if (totalHours === 0) return 0;
    return (totalMeters / 1000) / totalHours;
}

/**
 * ETA + 러너 상태 계산
 */
export function calculateEta({ history, destination, meetingStartMs, now }: EtaInput): EtaResult {
    const currentTs = now ?? Date.now();
    const latest = history[history.length - 1];
    const minutesUntilStart = Math.round((meetingStartMs - currentTs) / 60000);

    if (!latest) {
        return {
            distanceMeters: 0,
            speedKmh: 0,
            etaMinutes: null,
            arrived: false,
            minutesUntilStart,
            projectedLateMinutes: 0,
            state: 'stopped',
        };
    }

    const distanceMeters = haversineMeters(
        latest.latitude, latest.longitude,
        destination.latitude, destination.longitude,
    );

    const arrived = distanceMeters <= ARRIVE_RADIUS_M;
    if (arrived) {
        return {
            distanceMeters,
            speedKmh: 0,
            etaMinutes: 0,
            arrived: true,
            minutesUntilStart,
            projectedLateMinutes: 0,
            state: 'arrived',
        };
    }

    const speedKmh = estimateSpeedKmh(history);
    let etaMinutes: number | null = null;

    if (speedKmh >= MIN_SPEED_KMH) {
        const adjustedMeters = distanceMeters * ROUTE_DETOUR_FACTOR;
        etaMinutes = Math.round((adjustedMeters / 1000) / speedKmh * 60);
    }

    const projectedLateMinutes = etaMinutes != null
        ? Math.max(0, etaMinutes - minutesUntilStart)
        : Math.max(0, -minutesUntilStart); // 속도 0인데 이미 시각 지남 = 지각 중

    let state: RunnerState;
    if (speedKmh < MIN_SPEED_KMH) {
        state = 'stopped';
    } else if (projectedLateMinutes >= 10) {
        state = 'panic';
    } else if (projectedLateMinutes > 0) {
        state = 'rush';
    } else if (etaMinutes != null && etaMinutes <= minutesUntilStart - 5) {
        state = 'easy';
    } else {
        state = 'normal';
    }

    return {
        distanceMeters,
        speedKmh,
        etaMinutes,
        arrived: false,
        minutesUntilStart,
        projectedLateMinutes,
        state,
    };
}

/**
 * React 훅 — 입력값 바뀔 때만 재계산
 */
export default function useEta(input: EtaInput): EtaResult {
    return useMemo(() => calculateEta(input), [
        input.history,
        input.destination.latitude,
        input.destination.longitude,
        input.meetingStartMs,
        input.now,
    ]);
}
